import { memo, useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Image as ImageIcon, ImageOff, ChevronDown, ChevronUp } from 'lucide-react';
import {
  KnowledgeParticleQuestionRichBody,
  resolveKnowledgeParticleQaDisplay,
} from '../../components/KnowledgeParticleQaSections';
import type { TtqibCanvasNode } from './types';
import { TtqibEmbeddedImage } from './TtqibEmbeddedImage';
import { useParticleEmbeddedImage } from './useParticleEmbeddedImage';
import { hasFlowSequenceSteps, TtqibFlowSequenceStrip } from './TtqibFlowSequenceStrip';

type TtqibNodeProps = {
  data: TtqibCanvasNode;
  selected: boolean;
};

/** 颗粒节点总宽（步骤区 6 列无滚动） */
export const TTQIB_PARTICLE_NODE_WIDTH = 1200;
const NODE_MIN_HEIGHT = 200;

function particleHasPap(data: TtqibCanvasNode): boolean {
  return Boolean(data.pipelineEntry?.pap || data.particleMetadata);
}

function pipelineDotColor(status?: TtqibCanvasNode['pipelineStatus']): string {
  switch (status) {
    case 'done': return '#10b981';
    case 'pap_failed': return '#ef4444';
    case 'pending':
    case 'pap_running':
    case 'analyzing': return '#3b82f6';
    default: return '#9ca3af';
  }
}

function nodeToneStyle(kind: TtqibCanvasNode['layout']['kind']): { borderColor: string; backgroundColor: string } {
  switch (kind) {
    case 'derived': return { borderColor: '#f59e0b', backgroundColor: '#fffbeb' };
    case 'anchor': return { borderColor: '#3b82f6', backgroundColor: '#eff6ff' };
    case 'cttq': return { borderColor: '#8b5cf6', backgroundColor: '#f5f3ff' };
    case 'aggregate_a':
    case 'aggregate_b': return { borderColor: '#f59e0b', backgroundColor: '#fffbeb' };
    default: return { borderColor: '#d1d5db', backgroundColor: '#ffffff' };
  }
}

export const TtqibNode = memo(({ data, selected }: TtqibNodeProps) => {
  const kind = data.layout.kind;
  const isParticleNode = kind === 'anchor' || kind === 'pipeline_step' || kind === 'derived';
  const tone = nodeToneStyle(kind);

  const questionDisplay = useMemo(
    () => (isParticleNode
      ? resolveKnowledgeParticleQaDisplay({
        questionSource: data.particleQuestions,
        answerSource: null,
      }).questionDisplay
      : { main: '', items: [] as string[] }),
    [isParticleNode, data.particleQuestions],
  );
  const hasQuestionContent = Boolean(questionDisplay.main || questionDisplay.items.length);
  const imageOpen = Boolean(data.imageOpen);
  const { data: sourceImage, loading: sourceImageLoading } = useParticleEmbeddedImage(
    isParticleNode ? data.particleId : undefined,
    isParticleNode,
  );
  const hasEmbeddedCrop = Boolean(sourceImage?.crop);
  const hasFullSource = Boolean(sourceImage?.fullSourceUrl);
  const showBehaviorCheckbox = isParticleNode && particleHasPap(data);
  const hasFlowSteps = useMemo(
    () => hasFlowSequenceSteps(data.pipelineEntry),
    [data.pipelineEntry],
  );

  const handleOpenParticleDetail = (e: React.MouseEvent) => {
    e.stopPropagation();
    data.onOpenParticleDetail?.(data.layout.nodeId);
  };
  const handleToggleImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.particleId) data.onToggleImageOpen?.(data.particleId);
  };
  const handleToggleBehaviorSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.particleId) data.onToggleBehaviorParticle?.(data.particleId);
  };
  const handleToggleAdvancedMode = (e: React.MouseEvent) => {
    e.stopPropagation();
    data.onToggleAdvancedMode?.(data.layout.nodeId);
  };

  const isStepsExpanded = data.layout.collapsed === false;

  if (!isParticleNode) {
    const label = kind === 'cttq' ? 'CTTQ' : kind.startsWith('aggregate') ? '聚合' : '节点';
    return (
      <div
        className="rounded-lg border-2 bg-white shadow-sm p-4"
        style={{
          borderColor: selected ? '#3b82f6' : tone.borderColor,
          backgroundColor: tone.backgroundColor,
          width: 320,
          minHeight: 120,
        }}
      >
        <Handle type="target" position={Position.Left} id="target-left" style={{ opacity: 0 }} />
        <span className="text-xs font-bold text-gray-500 uppercase">{label}</span>
        <h3 className="text-sm font-semibold text-gray-900 mt-2 line-clamp-2">{data.title}</h3>
        {data.subtitle && <p className="text-xs text-gray-500 mt-1">{data.subtitle}</p>}
        <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      </div>
    );
  }

  return (
    <div
      className="relative flex flex-col rounded-lg border-2 bg-white shadow-sm"
      style={{
        borderColor: selected ? '#3b82f6' : tone.borderColor,
        backgroundColor: tone.backgroundColor,
        width: TTQIB_PARTICLE_NODE_WIDTH,
        minHeight: NODE_MIN_HEIGHT,
        boxShadow: selected ? '0 4px 12px rgba(59,130,246,0.2)' : undefined,
      }}
    >
      {imageOpen && hasFullSource && (
        <div
          className="absolute right-full top-0 z-20 mr-2 flex flex-col items-stretch gap-1 rounded-lg border border-green-300 bg-white p-2 shadow-lg"
          style={{ width: 320 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-1 pb-1 text-[10px] text-green-700">
            <span className="font-semibold">原始素材参考</span>
            <button type="button" onClick={handleToggleImage} className="rounded p-0.5 hover:bg-green-50" aria-label="收起原始素材">
              <ImageOff size={12} />
            </button>
          </div>
          {sourceImageLoading ? (
            <div className="flex h-40 items-center justify-center text-xs text-gray-400">加载中…</div>
          ) : (
            <TtqibEmbeddedImage
              fullSourceUrl={sourceImage?.fullSourceUrl}
              ossId={sourceImage?.ossId}
              height={220}
              width={300}
            />
          )}
        </div>
      )}

      <Handle type="target" position={Position.Left} id="target-left" style={{ opacity: 0 }} />

      <div
        className="w-full shrink-0 cursor-pointer p-4 transition-colors hover:bg-green-50/40"
        onClick={handleOpenParticleDetail}
        title="点击查看知识颗粒详情"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {hasFullSource ? (
              <button
                type="button"
                onClick={handleToggleImage}
                className={
                  'inline-flex h-6 w-6 items-center justify-center rounded-md border cursor-pointer '
                  + (imageOpen
                    ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100'
                    : 'border-green-300 bg-white text-green-600 hover:bg-green-50')
                }
                aria-label={imageOpen ? '收起原始素材' : '展开原始素材参考'}
                aria-pressed={imageOpen}
              >
                {imageOpen ? <ImageIcon size={14} /> : <ImageOff size={14} />}
              </button>
            ) : null}
            <span className={`text-xs font-bold uppercase ${kind === 'derived' ? 'text-amber-600' : 'text-green-600'}`}>
              {kind === 'derived' ? '衍生解法' : '原始题目'}
            </span>
          </div>
          {data.pipelineStatus && (
            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: pipelineDotColor(data.pipelineStatus) }} />
          )}
        </div>

        {sourceImageLoading ? (
          <div className="mb-3 flex h-28 items-center justify-center rounded-lg border border-dashed border-green-200 bg-green-50/40 text-xs text-muted">
            加载嵌入图…
          </div>
        ) : hasEmbeddedCrop ? (
          <div className="mb-3 rounded-lg border border-green-100 bg-white p-2">
            <p className="mb-1 text-[10px] font-bold uppercase text-green-600">嵌入图</p>
            <TtqibEmbeddedImage
              record={sourceImage?.crop}
              ossId={sourceImage?.ossId}
              height={140}
              width={300}
            />
          </div>
        ) : null}

        {hasQuestionContent ? (
          <div className="space-y-3">
            <KnowledgeParticleQuestionRichBody questionDisplay={questionDisplay} />
          </div>
        ) : (
          <p className="text-sm italic text-gray-400">暂无主问题</p>
        )}
        <p className="mt-3 text-[10px] text-green-600">点击查看颗粒详情 →</p>
      </div>

      {hasFlowSteps ? (
        <div className="border-t border-blue-100 bg-blue-50/20" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between gap-2 px-3 py-2">
            <span className="text-[11px] font-medium text-blue-700">高级品题步骤</span>
            <button
              type="button"
              onClick={handleToggleAdvancedMode}
              className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-white px-2 py-0.5 text-[10px] font-medium text-blue-600 hover:bg-blue-50"
              aria-expanded={isStepsExpanded}
            >
              {isStepsExpanded ? (
                <>
                  <ChevronUp size={12} />
                  收起
                </>
              ) : (
                <>
                  <ChevronDown size={12} />
                  展开
                </>
              )}
            </button>
          </div>
          {isStepsExpanded ? (
            <div className="nowheel nodrag border-t border-blue-100 bg-blue-50/30 px-3 pb-3 pt-2">
              <TtqibFlowSequenceStrip
                entry={data.pipelineEntry}
                nodeId={data.layout.nodeId}
                onInspectPipelineStep={data.onInspectPipelineStep}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {showBehaviorCheckbox ? (
        <label
          className="absolute right-[-18px] top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-2 border-blue-600 bg-white shadow-md hover:bg-blue-50"
          title={data.selectedForBehavior ? '取消选择该颗粒' : '选中该颗粒加入品题行为'}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer accent-blue-600"
            checked={Boolean(data.selectedForBehavior)}
            onChange={() => { /* onClick 负责 */ }}
            onClick={handleToggleBehaviorSelection}
            aria-label="选择该颗粒加入品题行为"
          />
        </label>
      ) : null}

      <Handle type="source" position={Position.Right} id="source-right" style={{ top: '40%', opacity: 0 }} />
    </div>
  );
});

TtqibNode.displayName = 'TtqibNode';
